"use client";

import React, { useState, useEffect, useCallback } from "react";
import { m as motion, PanInfo, AnimatePresence, useAnimation } from "framer-motion";
import Link from "next/link";
import Image from "next/image";
import { ShieldCheck, ArrowRight, Github, ExternalLink } from "lucide-react";
import { PhoneFrame, MockScreenContent, LaptopFrame } from "./PhoneCard";
import { getBlogPostUrl } from "@/lib/utils/blog-url";
import { cn } from "@/lib/utils";

interface ShippedCardProps {
  app: {
    id: string;
    name: string;
    description: string;
    techStack?: string[];
    blogSlug?: string | null;
    githubUrl?: string | null;
    liveUrl?: string | null;
    imageUrl?: string | null;
    screenshots?: string[];
    type?: "app" | "website";
    year?: string;
  };
  delay?: number;
  isFullWidth?: boolean;
  onClick?: () => void;
}

export const ShippedCard = ({
  app,
  delay = 0,
  isFullWidth = false,
  onClick,
}: ShippedCardProps) => {
  const [isHovered, setIsHovered] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [direction, setDirection] = useState(0);

  const fallbackScreenshots = [
    "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&q=80", 
    "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&q=80",
    "https://images.unsplash.com/photo-1555421689-491a97ff2040?w=800&q=80",
  ];

  const screenshots = app.screenshots && app.screenshots.length > 0 
    ? app.screenshots 
    : (app.imageUrl ? [app.imageUrl, ...fallbackScreenshots.slice(0,2)] : fallbackScreenshots);

  const handleNext = useCallback(() => {
    if (screenshots.length <= 1) return;
    setDirection(1);
    setCurrentImageIndex((prev) => (prev + 1) % screenshots.length);
  }, [screenshots.length]);

  const handlePrevious = useCallback(() => {
    if (screenshots.length <= 1) return;
    setDirection(-1);
    setCurrentImageIndex((prev) => (prev - 1 + screenshots.length) % screenshots.length);
  }, [screenshots.length]);

  const handleDragEnd = useCallback(
    (event: any, info: PanInfo) => {
      setIsDragging(false);
      const offset = info.offset.x;
      const velocity = info.velocity.x;

      if (Math.abs(offset) > 50 || Math.abs(velocity) > 500) {
        if (offset > 0 || velocity > 0) {
          handlePrevious();
        } else {
          handleNext();
        }
      }
    },
    [handleNext, handlePrevious]
  );
  
  // Animation variants for sliding images
  const slideVariants = {
    enter: (direction: number) => ({
      x: direction > 0 ? "100%" : "-100%",
      opacity: 0,
      scale: 0.95
    }),
    center: {
      x: 0,
      opacity: 1,
      scale: 1,
      zIndex: 1,
      transition: {
        x: { type: "spring", stiffness: 300, damping: 30 },
        opacity: { duration: 0.2 }
      }
    },
    exit: (direction: number) => ({
      x: direction < 0 ? "100%" : "-100%",
      opacity: 0,
      scale: 0.95,
      zIndex: 0,
      transition: {
        x: { type: "spring", stiffness: 300, damping: 30 },
        opacity: { duration: 0.2 }
      }
    })
  };

  // Auto-rotate images ONLY on hover
  useEffect(() => {
    if (!isHovered || isDragging || screenshots.length <= 1) return;

    const interval = setInterval(() => {
      handleNext();
    }, 3000); // 3 seconds per screen

    return () => clearInterval(interval);
  }, [isHovered, isDragging, handleNext, screenshots.length]);

  return (
    <motion.div
      initial={isFullWidth ? { opacity: 1, y: 0 } : { opacity: 0, y: 10 }}
      animate={isFullWidth ? { opacity: 1, y: 0 } : {}}
      whileInView={!isFullWidth ? { opacity: 1, y: 0 } : {}}
      viewport={{ once: true, margin: "-50px" }}
      transition={{
        duration: 0.4,
        ease: "easeOut",
        delay: isFullWidth ? 0 : delay,
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onTap={() => {
        if (!isDragging && onClick) {
          onClick();
        }
      }}
      className={cn(
        "group relative p-4 md:p-8 lg:p-10 transition-[background-color,border-color,box-shadow] duration-300 flex flex-col lg:flex-row gap-6 lg:gap-16 cursor-pointer will-change-transform transform-gpu overflow-hidden",
        isFullWidth 
          ? "w-full bg-transparent border-none shadow-none cursor-default pb-10 pt-4 md:py-8" 
          : "border border-border/50 dark:border-white/10 rounded-2xl bg-white/5 dark:bg-white/[0.02] hover:bg-white/10 dark:hover:bg-white/[0.05] hover:shadow-2xl backdrop-blur-md min-h-[600px] lg:min-h-[420px]"
      )}
    >


      {/* Background Decorative Text */}
      <div className={cn(
        "absolute top-0 -left-6 md:-left-8 font-black text-foreground/[0.02] select-none pointer-events-none leading-none -translate-y-4 md:-translate-y-16 uppercase z-0 break-all",
        isFullWidth ? "text-[5rem] sm:text-[10rem] md:text-[18rem]" : "text-[5rem] sm:text-[8rem] md:text-[18rem]"
      )}>
        {app.techStack?.some(t => t.toLowerCase().includes('flutter') || t.toLowerCase().includes('native')) ? 'App' : 'Web'}
      </div>

      {/* Content Side */}
      <div
        className={cn(
          "flex-1 relative z-10 flex flex-col justify-center",
          isFullWidth ? "space-y-4 md:space-y-6 lg:py-4" : "space-y-4 md:space-y-6"
        )}
      >
        {/* Top Info Bar */}
        <div className="flex items-center gap-3 mb-1 md:mb-4 text-left">
           <span className="text-[10px] md:text-[11px] font-bold tracking-[0.2em] text-primary uppercase">
            {app.year || "2026"}
          </span>
          <div className="h-px flex-1 bg-border/20" />
          <span className="text-[9px] md:text-[10px] font-medium text-muted-foreground/40 uppercase tracking-widest leading-none">
            {app.type || "Project"}
          </span>
        </div>

        {/* Title Area */}
        <div className="space-y-1 mb-2 md:mb-6">
          <motion.h3
            className={cn(
              "font-bold tracking-tight text-foreground leading-[1.1] transition-colors duration-300 group-hover:text-primary",
              isFullWidth ? "text-2xl sm:text-3xl md:text-5xl" : "text-xl sm:text-2xl md:text-4xl"
            )}
            animate={isHovered ? { x: 8 } : { x: 0 }}
            transition={{ type: "spring", stiffness: 200, damping: 20 }}
          >
            {app.name.includes(" - ") ? app.name.split(" - ")[0] : app.name}
            {app.name.includes(" - ") && (
              <span className={cn(
                "block font-normal text-muted-foreground/60 mt-1 md:mt-2",
                isFullWidth ? "text-[13px] sm:text-lg md:text-2xl" : "text-[11px] sm:text-sm md:text-xl"
              )}>
                {app.name.split(" - ")[1]}
              </span>
            )}
          </motion.h3>
        </div>

        {/* Description */}
        <div className="relative mb-4 md:mb-6 min-h-[100px] md:min-h-[100px]">
          <p className={cn(
            "text-muted-foreground/70 leading-relaxed font-medium line-clamp-3",
            isFullWidth ? "text-[13px] sm:text-sm md:text-base" : "text-[12px] sm:text-[13px] md:text-base"
          )}>
            {app.description}
          </p>
          
          <div className="flex items-center gap-2 mt-4 md:mt-4 text-[9px] md:text-[10px] font-bold text-foreground/40 uppercase tracking-[0.2em]">
            <div className="w-4 h-[1px] bg-primary/30" />
            <span>Fullstack & UI Design</span>
          </div>
        </div>

        {/* Tech Stack - Minimalist Hashtags */}
        <div className="flex flex-wrap gap-x-3 gap-y-2 pt-2">
          {app.techStack?.slice(0, 5).map((tag) => (
            <span
              key={tag}
              className="text-[10px] md:text-[11px] font-bold text-foreground/30 hover:text-primary transition-colors cursor-default"
            >
              #{tag}
            </span>
          ))}
          {app.techStack && app.techStack.length > 5 && (
            <span className="text-[10px] md:text-[11px] font-bold text-foreground/20 leading-none">
              +{app.techStack.length - 5}
            </span>
          )}
        </div>

        {/* Action Links */}
        <div className={cn(
          "flex flex-wrap items-center gap-4 md:gap-6",
          isFullWidth ? "pt-6 md:pt-10" : "pt-4 md:pt-8"
        )}>
          {(isFullWidth || (app.blogSlug && app.blogSlug.trim() !== "")) && app.blogSlug && (
            <Link
              href={getBlogPostUrl(app.blogSlug)}
              className="group/link inline-flex items-center gap-2 text-[10px] md:text-[11px] font-bold uppercase tracking-[0.2em] text-foreground/60 hover:text-primary transition-all duration-300"
            >
              <span>Case Study</span>
              <ArrowRight className="w-3 md:w-3.5 h-3 md:h-3.5 transition-transform group-hover/link:translate-x-1" />
            </Link>
          )}

          {app.githubUrl && app.githubUrl.trim() !== "" && (
            <a
              href={app.githubUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-[10px] md:text-[11px] font-bold text-foreground/60 hover:text-foreground transition-all"
            >
              <Github className="w-3.5 h-3.5 md:w-4 md:h-4" />
              Source
            </a>
          )}

          {app.liveUrl && app.liveUrl.trim() !== "" && (
            <a
              href={app.liveUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-[10px] md:text-[11px] font-bold text-primary hover:gap-2 transition-all"
            >
              Launch Project
              <ExternalLink className="w-3.5 h-3.5 md:w-4 md:h-4" />
            </a>
          )}
        </div>
      </div>

      {/* Visual Side - 3 Phones with Orbital Carousel OR 1 Laptop for Website */}
      <div
        className={cn(
          "flex-shrink-0 relative flex items-center",
          isFullWidth
            ? "h-[280px] md:h-[420px] lg:h-[500px] lg:w-[500px] justify-center"
            : "h-[300px] lg:h-[400px] lg:w-[420px] justify-center lg:-ml-4"
        )}
      >
        {app.type === "website" ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="w-full h-full flex items-center justify-center p-4 relative"
          >
            {/* Background Decorative Element for Web */}
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
              className="absolute w-64 h-64 bg-primary/5 rounded-full blur-3xl -z-10"
            />

            {/* Swipe/Drag Layer for Laptop */}
            <motion.div
              className="absolute inset-0 z-40 cursor-grab active:cursor-grabbing"
              drag="x"
              dragConstraints={{ left: 0, right: 0 }}
              dragElastic={0.2}
              onDragStart={() => setIsDragging(true)}
              onDragEnd={handleDragEnd}
              onTap={(e) => {
                // If it's a tap (not a drag), open the modal
                if (!isDragging && onClick) {
                   onClick();
                }
                e.stopPropagation();
              }}
            />

            <LaptopFrame className="w-full max-w-[240px] md:max-w-[450px]">
              <div className="w-full h-full relative bg-black overflow-hidden group/laptop">
                <AnimatePresence initial={false} custom={direction} mode="popLayout">
                  <motion.div
                    key={currentImageIndex}
                    custom={direction}
                    variants={slideVariants}
                    initial="enter"
                    animate="center"
                    exit="exit"
                    className="absolute inset-0 w-full h-full"
                  >
                    <motion.img
                      src={screenshots[currentImageIndex]}
                      alt={app.name}
                      className="w-full h-full object-cover object-top select-none pointer-events-none"
                      draggable={false}
                    />
                    {/* Inner Shadow for Screen Realism */}
                    <div className="absolute inset-0 shadow-[inset_0_0_20px_rgba(0,0,0,0.5)] pointer-events-none z-10" />
                  </motion.div>
                </AnimatePresence>
                
                {/* Carousel Indicators - macOS Style Dots */}
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-1.5 z-20 px-2 py-1 rounded-full bg-black/40 backdrop-blur-md border border-white/5 opacity-0 group-hover/laptop:opacity-100 transition-opacity duration-300">
                  {screenshots.map((_, idx) => (
                    <div 
                      key={idx}
                      className={cn(
                        "w-1.5 h-1.5 rounded-full transition-all duration-300",
                        idx === currentImageIndex ? "bg-white w-4" : "bg-white/30"
                      )}
                    />
                  ))}
                </div>
              </div>
            </LaptopFrame>
          </motion.div>
        ) : (
          <>
            {/* Swipe Layer - Prevent clicks here from opening the modal */}
            <motion.div
              className="absolute inset-0 z-40 cursor-grab active:cursor-grabbing"
              drag="x"
              dragConstraints={{ left: 0, right: 0 }}
              dragElastic={0.2}
              onDragStart={() => setIsDragging(true)}
              onDragEnd={handleDragEnd}
              onTap={(e) => e.stopPropagation()}
              onClick={(e) => e.stopPropagation()}
            />

            <div className="relative w-full h-full flex items-center justify-center pointer-events-none">
              {[0, 1, 2].map((idx) => {
                // Determine slot position (0: center, 1: right, 2: left)
                // We use a double modulo to handle negative results and larger screenshot arrays correctly
                const position = ((idx - (currentImageIndex % 3) + 3) % 3);
                const isCenter = position === 0;
                const isLeft = position === 2;
                const isRight = position === 1;

                // To ensure ALL screenshots are shown, we determine what image this phone currently holds
                // based on which slot it's in.
                const screenshotIdx = (currentImageIndex + (isCenter ? 0 : isRight ? 1 : -1) + screenshots.length) % screenshots.length;
                const screenshot = screenshots[screenshotIdx];

                return (
                  <motion.div
                    key={idx}
                    className="absolute left-1/2 top-1/2"
                    initial={false}
                    animate={
                      isCenter
                        ? {
                            x: "-50%",
                            y: "-50%",
                            scale: isFullWidth ? 1.15 : 1,
                            rotate: 0,
                            zIndex: 30,
                            opacity: 1,
                            filter: "blur(0px)",
                          }
                        : isLeft
                        ? {
                            x: isFullWidth ? "-135%" : "-130%",
                            y: "-40%",
                            scale: isFullWidth ? 0.9 : 0.85,
                            rotate: -10,
                            zIndex: 10,
                            opacity: 0.8,
                            filter: "blur(0.5px)",
                          }
                        : {
                            x: isFullWidth ? "35%" : "30%",
                            y: "-40%",
                            scale: isFullWidth ? 0.9 : 0.85,
                            rotate: 10,
                            zIndex: 10,
                            opacity: 0.8,
                            filter: "blur(0.5px)",
                          }
                    }
                    whileHover={isCenter ? { scale: isFullWidth ? 1.2 : 1.05, y: "-55%" } : {}}
                    transition={{
                      type: "spring",
                      stiffness: 110,
                      damping: 18,
                      mass: 1,
                    }}
                  >
                    <PhoneFrame
                      className={`shadow-2xl border-gray-900 dark:border-black transition-all duration-500 ${
                        isFullWidth
                          ? isCenter
                            ? "!w-[118px] !h-[255px] md:!w-[150px] md:!h-[325px] lg:!w-[185px] lg:!h-[400px]"
                            : "!w-[98px] !h-[210px] md:!w-[118px] md:!h-[253px] lg:!w-[140px] lg:!h-[300px]"
                          : isCenter
                            ? "!w-[140px] !h-[300px] md:!w-[147px] md:!h-[320px] lg:!w-[155px] lg:!h-[340px]"
                            : "!w-[115px] !h-[250px] md:!w-[125px] md:!h-[270px] lg:!w-[135px] lg:!h-[290px]"
                      }`}
                    >
                      <MockScreenContent
                        type="image"
                        title={app.name}
                        imageUrl={screenshot}
                      />
                    </PhoneFrame>
                  </motion.div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </motion.div>
  );
};
